import { BBox, FeatureCollection, Length, Location, SaveFeatureCollection } from '@agroone/entities'
import { ElementRef, inject, Injectable, signal } from '@angular/core'
import type MapboxDraw from '@mapbox/mapbox-gl-draw'
import { TranslateService } from '@ngx-translate/core'
import { AllGeoJSON, area, bbox } from '@turf/turf'
import configureMeasurements, { allMeasures, type AreaUnits } from 'convert-units'
import { BoundaryOfInterest, PointOfInterest } from '../../../models/map-box.model'
import { LoggerService } from '../../../services/logger.service'
import { SharedConstantService } from '../../../services/shared-constant.service'
import { SharedUserService } from '../../../services/shared-user.service'
import { requireDefined } from '../../../utils/require-defined.util'
import type * as mapboxgl from 'mapbox-gl'
import { BehaviorSubject, debounceTime, finalize, map, Observable } from 'rxjs'
import { AoConfirmDialogData } from '../../ao-confirm-dialog/ao-confirm-dialog.component'
import { MAP_STYLES } from '../const/mapStyles.const'
import { DisplayModeControl } from '../controls/display-mode.control'
import { MapStyleControl } from '../controls/map-style.control'
import { MapStyleType } from '../enums/map-style-type.enum'
import { AreaPopup } from '../interfaces/area-popup.interface'
import { ClickedObject } from '../interfaces/clicked-object.model'
import { DisplayModeOption } from '../interfaces/display-mode-option.interface'
import { MapInitConfig } from '../interfaces/map-init-config.interface'
import { MapBoxOptions } from '../interfaces/mapbox-options.interface'
import { loadMapboxDraw, loadMapboxGl, MapboxDrawConstructor, MapboxGlModule } from '../utils/mapbox-loader.util'
import { MapGeoService } from './map-geo.service'
import { MapTokenService } from './map-token.service'

const convert = configureMeasurements(allMeasures)

type SavedGeoJSONSource = {
  type: 'geojson'
  data: GeoJSON.GeoJSON | string
  layers?: mapboxgl.LayerSpecification[]
}

/** Container width assumed when it has none yet (not laid out): mobile-sized. */
const FALLBACK_CONTAINER_WIDTH_PX = 360

@Injectable({ providedIn: 'root' })
export class MapCoreService {
  private _map?: mapboxgl.Map
  private _draw?: MapboxDraw
  private _mapInitiated = new BehaviorSubject<boolean>(false)
  private _viewportSubject = new BehaviorSubject<{ viewport: BBox; zoom: number } | null>(null)
  private _defaultMarker?: mapboxgl.Marker
  private _defaultAreaUnit?: string
  private areaPopups: AreaPopup[] = []
  private _drawEventsBound = false
  private _manualLocation: Location = new Location({
    longitude: '0',
    latitude: '0',
  })
  private satelliteStyle: string = 'mapbox://styles/mapbox-bonduelle/cmlgdk3ef001901scfivs3ioo'
  private standardStyle: string = 'mapbox://styles/mapbox-bonduelle/cmlgauxfc00ez01qxa2j2h0jk'
  private currentStyleType: MapStyleType = 'satellite'
  private styleControl: MapStyleControl | null = null
  private displayModeControl: DisplayModeControl | null = null

  private readonly _tokenService = inject(MapTokenService)
  private readonly _mapGeoService = inject(MapGeoService)
  private readonly _userService = inject(SharedUserService)
  private readonly _constantService = inject(SharedConstantService)
  /** Read by `MapDrawComponent` to declaratively host the delete-boundary confirm dialog. */
  public readonly confirmDialogOpen = signal(false)
  public readonly confirmDialogData = signal<AoConfirmDialogData | null>(null)
  private _confirmDialogResolve?: (confirmed: boolean) => void
  private readonly _logger = inject(LoggerService)
  private readonly _translate = inject(TranslateService)

  // Mapbox libraries are heavy (~1.8MB) and are loaded lazily on first map usage
  // (see `loadMapboxGl` / `loadMapboxDraw`) so they stay out of the initial bundle.
  private mapboxgl!: MapboxGlModule
  private MapboxDraw!: MapboxDrawConstructor

  private async _loadMapbox(): Promise<void> {
    ;[this.mapboxgl, this.MapboxDraw] = await Promise.all([loadMapboxGl(), loadMapboxDraw()])
  }

  get map(): mapboxgl.Map | undefined {
    return this._map
  }

  /** The current map, for code paths that are only reachable once `init` has created it. */
  private _requireMap(): mapboxgl.Map {
    return requireDefined(this._map, 'the map to be initialized')
  }

  get mapInitiated$(): Observable<boolean> {
    return this._mapInitiated.asObservable()
  }

  /**
   * Emits the viewport (bbox) and zoom on every map navigation event
   * (moveend, zoomend, resize, …) — see `_initMapEvents`.
   */
  get viewportChanges$(): Observable<{ viewport: BBox; zoom: number } | null> {
    return this._viewportSubject.asObservable()
  }

  // =====================================================================
  // INIT
  // =====================================================================
  public async init(config: MapInitConfig): Promise<void> {
    const { container, boundary, center } = config
    const [, token] = await Promise.all([this._loadMapbox(), this._tokenService.ensureToken()])
    const bounds = boundary?.bbox as mapboxgl.LngLatBoundsLike
    const mapCenter = center ?? { latitude: 0, longitude: 0 }

    // A new Map instance invalidates everything bound to the previous one
    // (draw control, popups, native controls): reset before recreating.
    this._resetMapState()

    this._manualLocation.longitude = mapCenter.longitude?.toString()
    this._manualLocation.latitude = mapCenter.latitude?.toString()

    const map = new this.mapboxgl.Map({
      container: container?.nativeElement,
      accessToken: token,
      style: this.satelliteStyle,
      center: [mapCenter.longitude, mapCenter.latitude],
      bounds,
      zoom: this._computeDefaultZoom(container?.nativeElement?.offsetWidth),
    })
    this._map = map

    this._addBaseControls(map)

    map.on('styledata', () => {
      this._mapInitiated.next(true)
      this._mapInitiated.complete()
    })

    // If geolocate
    if (config.geolocate) {
      this._addGeolocateControl(map)
    }

    // If Drawable
    this._drawMap(config.drawable, map)
    // If no boundaries
    if (!boundary) {
      this.drawDefaultMarker(mapCenter.latitude, mapCenter.longitude, config.draggable)
    }

    this._initMapEvents()
    // !!! DO NOT REMOVE this message. It's caught by the automated Playwright tests in order to know when mapbox has been initialised
    this._logger.debug('Map - init map done')

    return this._waitMapLoad(map, config)
  }

  /**
   * Detaches the previous map and clears the state bound to it, so a new
   * `init` starts clean (the service is a root singleton used by several
   * screens that don't always destroy it explicitly).
   */
  private _resetMapState(): void {
    if (this._map) {
      this._map.remove()
      this._map = undefined
    }
    this._draw = undefined
    this._drawEventsBound = false
    for (const ap of this.areaPopups) {
      ap.popup.remove()
    }
    this.areaPopups = []
    this.styleControl = null
    this.displayModeControl = null
    if (this._mapInitiated.isStopped) {
      this._mapInitiated = new BehaviorSubject<boolean>(false)
    }
  }

  private _waitMapLoad(map: mapboxgl.Map, options?: MapBoxOptions): Promise<void> {
    return new Promise((resolve, reject) => {
      map.on('load', () => {
        if (options?.stencil) this._addStencil(options.stencil)
        resolve()
      })

      map.on('error', (err) => reject(err))
    })
  }

  public resetMap() {
    if (this._draw) {
      const map = this._requireMap()
      map.removeControl(this._draw)
      map.remove()
      this._map = undefined
      this._draw = undefined
      this._drawEventsBound = false
    }
  }

  private _addStencil(stencil: FeatureCollection | SaveFeatureCollection) {
    const firstBoundaryId = this._getFirstBoundary()
    // entity features are looser than GeoJSON (string `type`, optional geometry), and SaveFeatureCollection's are optional
    this.addSource('stencil', stencil.features as GeoJSON.Feature[])
    this.addLayer('stencil', 'stencil', MAP_STYLES.STENCIL.color, MAP_STYLES.STENCIL.opacity, firstBoundaryId)
  }

  private _getFirstBoundary(): string | undefined {
    const map = this.map
    if (!map) return ''

    return map
      ?.getStyle()
      ?.layers?.map((layer) => layer.id)
      ?.filter((id) => id.startsWith('boundaries'))
      ?.sort((a, b) => a.localeCompare(b))?.[0]
  }

  private _addBaseControls(map: mapboxgl.Map) {
    map.addControl(new this.mapboxgl.NavigationControl())
    map.addControl(new this.mapboxgl.ScaleControl())
    // Add map style toggle control (pass the current style type)
    this.styleControl = new MapStyleControl(() => this.toggleMapStyle(), this.currentStyleType)
    map.addControl(this.styleControl, 'top-right')
  }

  private _addGeolocateControl(map: mapboxgl.Map) {
    map.addControl(
      new this.mapboxgl.GeolocateControl({
        positionOptions: { enableHighAccuracy: true },
        trackUserLocation: true,
      }),
    )
  }

  private _drawMap(drawable: boolean | undefined, map: mapboxgl.Map) {
    if (!map || !drawable) return

    this._draw = new this.MapboxDraw({
      displayControlsDefault: false,
      controls: { polygon: true, trash: true },
    })
    map.addControl(this._draw)
    this._bindDrawEvents(map)
  }

  private _bindDrawEvents(map: mapboxgl.Map) {
    if (this._drawEventsBound) return

    map.on('draw.create', (e: MapboxDraw.DrawCreateEvent) => this._manageAreaPopup(e))
    map.on('draw.update', (e: MapboxDraw.DrawUpdateEvent) => this._manageAreaPopup(e))
    map.on('draw.delete', (e: MapboxDraw.DrawDeleteEvent) => this._manageAreaPopup(e))
    this._drawEventsBound = true
  }

  public async initByAddress(container: ElementRef, address: string, options?: MapBoxOptions) {
    const feature = await this._mapGeoService.forwardGeocode(address)

    const coords = {
      longitude: feature?.center?.[0] ?? 0,
      latitude: feature?.center?.[1] ?? 0,
    }

    // The geocoded center is approximate, so the marker defaults to draggable;
    // an explicit `draggable` from the caller wins
    return this.init({ container, center: coords, draggable: true, ...(options ?? {}) })
  }

  // =====================================================================
  // DESTROY
  // =====================================================================
  public destroy(): void {
    if (this._map) {
      this._map.remove()
      this._map = undefined
      this._draw = undefined
      this._drawEventsBound = false
      // Clean any existing area popups
      for (const ap of this.areaPopups) {
        ap.popup.remove()
      }
      this.areaPopups = []
      this._mapInitiated.complete()
    }
  }

  // =====================================================================
  // COMPUTE ZOOM
  // =====================================================================
  /**
   * Zoom framing the user's geolocation radius across `containerWidth` pixels.
   * A hidden or unrendered container has no usable width: assume a plausible one.
   */
  private _computeDefaultZoom(containerWidth: number | undefined): number {
    const width = containerWidth || FALLBACK_CONTAINER_WIDTH_PX
    const user = this._userService.requireCurrentUser()
    const radius = user.geolocationRadius || 5
    const unit = user.geolocationRadiusUnit ?? Length.KM

    const radiusKm = unit === Length.KM ? radius : radius / 0.621371
    const scaleValue = (radiusKm * 2000) / width

    // https://docs.mapbox.com/help/glossary/zoom-level/
    const zoomLevels = [
      { level: 22, size: 0.019 },
      { level: 21, size: 0.037 },
      { level: 20, size: 0.075 },
      { level: 19, size: 0.149 },
      { level: 18, size: 0.299 },
      { level: 17, size: 0.597 },
      { level: 16, size: 1.194 },
      { level: 15, size: 2.389 },
      { level: 14, size: 4.777 },
      { level: 13, size: 9.555 },
      { level: 12, size: 19.109 },
      { level: 11, size: 38.218 },
      { level: 10, size: 76.437 },
      { level: 9, size: 152.874 },
      { level: 8, size: 305.748 },
      { level: 7, size: 611.496 },
      { level: 6, size: 1222.992 },
      { level: 5, size: 2445.984 },
      { level: 4, size: 4891.968 },
      { level: 3, size: 9783.936 },
      { level: 2, size: 19567.871 },
      { level: 1, size: 39135.742 },
      { level: 0, size: 78271.484 },
    ]
    // A radius larger than the widest zoom level leaves no match: fall back to the world view.
    return zoomLevels.find((z) => z.size >= scaleValue)?.level ?? 0
  }

  /**
   * Frames the map on the user's geolocation radius, using the container's width *now*.
   * A screen that creates the map inside a hidden container must call this once the map
   * is visible: the zoom computed at `init` was based on a container without a real width.
   */
  public applyDefaultZoom(): void {
    if (!this._map) return

    this._map.setZoom(this._computeDefaultZoom(this._map.getContainer()?.offsetWidth))
  }

  /**
   * Frames the map on the given items instead of the user's position: centers on the
   * single one (same zoom as the geolocation-radius default), or fits the viewport to all
   * of them when several are given. Falls back to a location's boundary centroid when it
   * has no `location` (not all crops/fields have both). Returns false (no-op) if the map
   * isn't initialized yet or none of the items are geolocated — the caller should then
   * fall back to `applyDefaultZoom`.
   */
  public frameOnLocatedItems(
    items: { location?: Location | null; boundary?: FeatureCollection | SaveFeatureCollection | null }[],
  ): boolean {
    if (!this._map) return false
    const features = this._locatedItemsToGeoFeatures(items)
    if (!features.length) return false

    if (items.length === 1) {
      const box = bbox({ type: 'FeatureCollection', features } as AllGeoJSON)
      this._map.jumpTo({
        center: [(box[0] + box[2]) / 2, (box[1] + box[3]) / 2],
        zoom: this._computeDefaultZoom(this._map.getContainer()?.offsetWidth),
      })
      return true
    }

    const box = bbox({ type: 'FeatureCollection', features } as AllGeoJSON) as [number, number, number, number]
    this._map.fitBounds(box, { padding: 64, maxZoom: 17, duration: 0 })
    return true
  }

  private _locatedItemsToGeoFeatures(
    items: { location?: Location | null; boundary?: FeatureCollection | SaveFeatureCollection | null }[],
  ): GeoJSON.Feature[] {
    const features: GeoJSON.Feature[] = []
    for (const item of items) {
      const latitude = Number(item.location?.latitude)
      const longitude = Number(item.location?.longitude)
      if (item.location && Number.isFinite(latitude) && Number.isFinite(longitude) && (latitude || longitude)) {
        features.push({
          type: 'Feature',
          properties: {},
          geometry: { type: 'Point', coordinates: [longitude, latitude] },
        })
      } else if (item.boundary?.features?.length) {
        features.push(...(item.boundary.features as unknown as GeoJSON.Feature[]))
      }
    }
    return features
  }

  // =====================================================================
  // EVENTS
  // =====================================================================
  private _initMapEvents() {
    const map = this.map
    if (!map) return

    const callback = () => {
      const bounds = requireDefined(map.getBounds(), 'the map bounds')
      const viewport: BBox = [bounds.getWest(), bounds.getSouth(), bounds.getEast(), bounds.getNorth()] as BBox
      this._viewportSubject.next({ viewport, zoom: map.getZoom() })
    }

    map.on('boxzoomend', callback)
    map.on('moveend', callback)
    map.on('pitchend', callback)
    map.on('resize', callback)
    map.on('rotateend', callback)
    map.on('zoomend', callback)
    callback()
  }

  // =====================================================================
  // MARKERS
  // =====================================================================
  public drawDefaultMarker(latitude: number, longitude: number, draggable?: boolean) {
    if (!this.map) return

    // Remove existing marker if any
    this._defaultMarker?.remove()

    // Create marker
    const marker = new this.mapboxgl.Marker({ draggable })
    this._defaultMarker = marker

    // Handle draggable event
    if (draggable) {
      marker.on('dragend', () => {
        const lngLat: mapboxgl.LngLat = marker.getLngLat()
        this._manualLocation.longitude = lngLat?.lng?.toString()
        this._manualLocation.latitude = lngLat?.lat?.toString()
      })
    }
    marker.setLngLat([longitude, latitude]).addTo(this.map)
  }

  /** Creates a source + a circle layer for points of interest (pinpoints). */
  public createMarkers(name: string, style: string, pointsOfInterest?: PointOfInterest[]) {
    let geoJsonData: GeoJSON.Feature<GeoJSON.Point>[] = []
    if (pointsOfInterest) {
      geoJsonData = this._mapToGeoJsonData(style, pointsOfInterest)
    }
    this.map?.addSource(name, {
      type: 'geojson',
      data: {
        type: 'FeatureCollection',
        features: geoJsonData,
      },
    })
    this.map?.addLayer({
      id: name,
      type: 'circle',
      source: name,
      paint: {
        // make circles larger as the user zooms from z12 to z22
        'circle-radius': {
          base: 4,
          stops: [
            [12, 6],
            [22, 9],
          ],
        },
        'circle-color': 'white',
        'circle-stroke-color': style,
        'circle-stroke-width': {
          base: 3,
          stops: [
            [12, 5],
            [22, 8],
          ],
        },
      },
    })
  }

  public updateMarkers(name: string, style: string, pointsOfInterest: PointOfInterest[]) {
    const geoJsonData = this._mapToGeoJsonData(style, pointsOfInterest)
    const source: mapboxgl.GeoJSONSource = this.map?.getSource(name) as mapboxgl.GeoJSONSource
    if (!source) {
      return
    }
    source.setData({
      type: 'FeatureCollection',
      features: geoJsonData,
    })
  }

  private _mapToGeoJsonData(style: string, pointsOfInterest: PointOfInterest[]): GeoJSON.Feature<GeoJSON.Point>[] {
    const geoJsonDatas: GeoJSON.Feature<GeoJSON.Point>[] = []
    for (const p of pointsOfInterest) {
      if (p.location) {
        const geoJson: GeoJSON.Feature<GeoJSON.Point> = {
          type: 'Feature',
          properties: { 'marker-color': style, id: p.id },
          geometry: {
            type: 'Point',
            coordinates: [+p.location.longitude, +p.location.latitude],
          },
        }
        geoJsonDatas.push(geoJson)
      }
    }
    return geoJsonDatas
  }

  // =====================================================================
  // INTERACTIONS
  // =====================================================================

  public resize(): void {
    this.map?.resize()
  }

  public setMapCursor(cursor: string): void {
    if (this.map) {
      this.map.getCanvas().style.cursor = cursor
    }
  }

  /** Last "manual" position: the map center, updated when the default marker is dragged. */
  public getManualLocation(): Location {
    return this._manualLocation
  }

  /** Browser GPS position, or undefined if unavailable/denied. */
  public getCurrentPosition(): Promise<Location | undefined> {
    return new Promise((resolve) => {
      if (!navigator.geolocation) {
        resolve(undefined)
        return
      }
      navigator.geolocation.getCurrentPosition(
        (position) =>
          resolve(
            new Location({
              latitude: String(position.coords.latitude),
              longitude: String(position.coords.longitude),
            }),
          ),
        () => resolve(undefined),
        { enableHighAccuracy: false, timeout: 10_000, maximumAge: 30_000 },
      )
    })
  }

  public getActiveFeaturesForLayers(layers: string[]) {
    return this._requireMap().queryRenderedFeatures({ layers })
  }

  /** Emits the clicked object (pinpoint or boundary) among the given layers. */
  public featureClicks(layers: string[]): Observable<ClickedObject> {
    let callback: (e: mapboxgl.MapMouseEvent | mapboxgl.MapTouchEvent) => void
    const obs: Observable<ClickedObject> = new Observable((subscriber) => {
      callback = (e) => {
        const features: GeoJSON.Feature[] = this._requireMap().queryRenderedFeatures(e.point, {
          layers,
        })
        if (features?.length > 0) {
          const clickedObject: ClickedObject = new ClickedObject(features[0])
          this._logger.debug(
            'Point clicked: id= ' + clickedObject.id + ', coordinates: ' + JSON.stringify(features[0].geometry),
          )

          subscriber.next(clickedObject)
        }
      }
      this._requireMap().on('touchend', callback)
      this._requireMap().on('click', callback)
    })

    return obs.pipe(
      finalize(() => {
        // Remove the listeners when the observable is ended
        this._requireMap().off('touchend', callback)
        this._requireMap().off('click', callback)
      }),
    )
  }

  /** Emits the clicked map position (regardless of any feature). */
  public onMapClick(): Observable<Location> {
    return new Observable<Location>((subscriber) => {
      const callback = (e: mapboxgl.MapMouseEvent | mapboxgl.MapTouchEvent) => {
        subscriber.next(
          new Location({
            latitude: e.lngLat.lat.toString(),
            longitude: e.lngLat.lng.toString(),
          }),
        )
      }
      this._requireMap().on('click', callback)
      this._requireMap().on('touchend', callback)
      return () => {
        this.map?.off('click', callback)
        this.map?.off('touchend', callback)
      }
    })
  }

  /** Emits the rendered features of the given layers after each render (throttled to 50ms). */
  public featureChanges(...layers: string[]): Observable<{ features: GeoJSON.Feature[]; zoom: number }> {
    let render: () => void

    const obs = new Observable<void>((subscriber) => {
      render = () => {
        subscriber.next()
      }
      this._requireMap().on('render', render)
    })
    return obs.pipe(
      // Debounce the render bursts: emit once rendering has settled for 50ms
      debounceTime(50),
      map(() => {
        const features: GeoJSON.Feature[] = this._requireMap().queryRenderedFeatures({ layers })
        return { features, zoom: this._requireMap().getZoom() }
      }),
      finalize(() => {
        // Remove the listeners when the observable is ended
        this._requireMap().off('render', render)
      }),
    )
  }

  // =====================================================================
  // DISPLAY MODE CONTROL
  // =====================================================================

  /**
   * Adds a native Mapbox display mode toggle control (e.g. crops / fields).
   * The callback fires only on user action; programmatic sync goes through
   * `setDisplayMode`.
   */
  public addDisplayModeControl(
    options: DisplayModeOption[],
    initialId: string,
    onChange: (id: string) => void,
    position: 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right' = 'top-left',
  ): void {
    if (!this.map) {
      this._logger.warn('Map - Cannot add display mode control: map not initialized')
      return
    }
    if (this.displayModeControl) {
      this.map.removeControl(this.displayModeControl)
    }
    this.displayModeControl = new DisplayModeControl(options, initialId, onChange)
    this.map.addControl(this.displayModeControl, position)
  }

  /** Syncs the toggle control's active option without triggering its callback. */
  public setDisplayMode(id: string): void {
    this.displayModeControl?.setActive(id)
  }

  /** Updates the toggle control's labels (e.g. on language change). */
  public setDisplayModeLabels(labels: Record<string, string>): void {
    this.displayModeControl?.setLabels(labels)
  }

  // =====================================================================
  // SOURCES
  // =====================================================================
  public addSource(name: string, features: GeoJSON.Feature[]) {
    this.map?.addSource(name, {
      type: 'geojson',
      data: {
        type: 'FeatureCollection',
        features,
      },
    })
  }

  public updateSource(name: string, features: GeoJSON.Feature[]) {
    const source: mapboxgl.GeoJSONSource = this.map?.getSource(name) as mapboxgl.GeoJSONSource
    if (source) {
      source.setData({ type: 'FeatureCollection', features })
    }
  }

  // =====================================================================
  // BOUNDARIES
  // =====================================================================

  public getBoundary() {
    if (this._draw?.getAll()?.features?.length) {
      const boundary: GeoJSON.FeatureCollection = this._draw.getAll()
      if (boundary) {
        // Calculate the bounding box
        const turfBbox = bbox(boundary as AllGeoJSON)
        boundary.bbox = turfBbox
      }
      return boundary
    }

    return null
  }

  public createBoundaries(
    name: string,
    boundary?: {
      [style: string]: { boundaries: BoundaryOfInterest[] }
    },
    boundaryOfInterest?: {
      style: string
      boundaries: BoundaryOfInterest[]
    },
    disabled: boolean = false,
  ) {
    if (boundaryOfInterest) {
      const geoJsonData = this._convertBoundariesMapToFeatureList(boundaryOfInterest.boundaries)
      this.addSource(name, geoJsonData)
      this.addLayer(name, name, boundaryOfInterest.style, disabled ? 0.8 : 0.5, undefined, '#000000')
    } else if (boundary) {
      for (const [style, b] of Object.entries(boundary)) {
        const geoJsonData = this._convertBoundariesMapToFeatureList(b.boundaries)
        const nameWithStyle = `${name}${style}`
        this.addSource(nameWithStyle, geoJsonData)
        this.addLayer(nameWithStyle, nameWithStyle, style, 0.7, undefined, '#000000')
      }
    }
  }

  public updateBoundaries(name: string, boundaries: BoundaryOfInterest[]) {
    const geoJsonData = this._convertBoundariesMapToFeatureList(boundaries)
    this.updateSource(name, geoJsonData)
  }

  async createEditableBoundaries(
    boundary?: FeatureCollection | SaveFeatureCollection | null,
    defaultAreaUnit?: string,
    allowControls?: boolean,
  ): Promise<void> {
    await this._loadMapbox()
    // Store the defaultAreaUnit for future use when creating new polygons
    if (defaultAreaUnit && !this._defaultAreaUnit) {
      this._defaultAreaUnit = defaultAreaUnit
    }

    if (!this._draw) {
      this._draw = new this.MapboxDraw({
        displayControlsDefault: false,
        controls: {
          polygon: allowControls,
          trash: allowControls,
        },
      })
      this._requireMap().addControl(this._draw)
    }

    // Ensure map and draw control are fully loaded before adding features
    return new Promise((resolve) => {
      if (this._draw && boundary?.features?.length) {
        this._draw.add(boundary as unknown as GeoJSON.FeatureCollection)
        // Use draw.getAll() instead of boundary.features: MapboxDraw may assign new IDs
        // to features that lacked one, so event.features (draw.update/create) will use
        // those canonical IDs. Using the original boundary.features risks id=undefined,
        // which breaks the deduplication logic and leaves only one popup visible.
        const drawnFeatures = this._draw.getAll().features
        this.addAreaPopup(drawnFeatures, defaultAreaUnit)
      }

      // Set up event listeners for area management (bind once)
      this._bindDrawEvents(this._requireMap())

      resolve()
    })
  }

  private _convertBoundariesMapToFeatureList(boundariesOfInterest: BoundaryOfInterest[]): GeoJSON.Feature[] {
    const featureList: GeoJSON.Feature[] = []
    for (const boundaryOfInterest of boundariesOfInterest) {
      for (const feature of boundaryOfInterest?.boundary?.features ?? []) {
        // entity features are looser than GeoJSON (string `type`, optional geometry and coordinates)
        featureList.push({
          ...feature,
          properties: { id: boundaryOfInterest.id },
        } as GeoJSON.Feature)
      }
    }

    return featureList
  }

  // =====================================================================
  // LAYERS
  // =====================================================================
  public addLayer(
    id: string,
    source: string,
    color: string,
    opacity: number,
    beforeId?: string,
    outlineColor?: string,
  ) {
    const map = this.map
    if (!map) return

    const layer: mapboxgl.FillLayer = {
      id,
      type: 'fill',
      source,
      paint: {
        'fill-color': color,
        'fill-opacity': opacity,
        ...(outlineColor && { 'fill-outline-color': outlineColor }),
      },
    }

    if (beforeId) {
      map.addLayer(layer, beforeId)
    } else {
      map.addLayer(layer)
    }
  }

  public getCenterLocationFromFeatures(features: GeoJSON.Feature<GeoJSON.Polygon>[]): Location | null {
    const bbox = this._getBoundingBox(features)
    if (!bbox) {
      return null
    }
    const center = {
      latitude: (bbox[1] + bbox[3]) / 2,
      longitude: (bbox[0] + bbox[2]) / 2,
    }

    if (center) {
      return {
        latitude: center.latitude.toString(),
        longitude: center.longitude.toString(),
      }
    }
    return null
  }

  private _getBoundingBox(features: GeoJSON.Feature<GeoJSON.Polygon>[]) {
    if (!features?.length) {
      return undefined
    }

    const bBox = bbox({
      type: 'FeatureCollection',
      features,
    })
    return bBox
  }

  public async createGeohashes(
    location?: { latitude: number | string; longitude: number | string } | null,
    boundary?: GeoJSON.FeatureCollection | FeatureCollection | SaveFeatureCollection | null,
    address?: string | null,
  ): Promise<string[] | null> {
    return await this._mapGeoService.createGeohashes(location, boundary, address)
  }

  // =====================================================================
  // AREA POPUPS
  // =====================================================================

  /** Resolves the pending {@link _confirmDialog} promise — called by `MapDrawComponent` on `(result)`. */
  public onConfirmDialogResult(confirmed: boolean): void {
    this.confirmDialogOpen.set(false)
    this._confirmDialogResolve?.(confirmed)
    this._confirmDialogResolve = undefined
  }

  /** Opens the shared confirm dialog hosted by `MapDrawComponent` and resolves once answered. */
  private _confirmDialog(data: AoConfirmDialogData): Promise<boolean> {
    // Only one confirm dialog can be shown at a time: resolve any still-pending call as
    // cancelled rather than silently orphaning its promise.
    this._confirmDialogResolve?.(false)
    this.confirmDialogData.set(data)
    this.confirmDialogOpen.set(true)
    return new Promise((resolve) => {
      this._confirmDialogResolve = resolve
    })
  }

  private _manageAreaPopup(
    event: MapboxDraw.DrawCreateEvent | MapboxDraw.DrawUpdateEvent | MapboxDraw.DrawDeleteEvent,
  ) {
    if (event.type === 'draw.delete') {
      const message: string = this._translate.instant('DIALOG.FIELD.BOUNDARY_DELETE.MESSAGE')

      this._confirmDialog({ message, displayCancelButton: true }).then((confirmed) => {
        const feature = event.features?.[0]
        if (!feature) return

        if (confirmed) {
          const popupIndex = this.areaPopups.findIndex((p) => p.id === feature.id)
          if (popupIndex > -1) {
            this.areaPopups[popupIndex].popup.remove()
            this.areaPopups.splice(popupIndex, 1)
          }
        } else {
          requireDefined(this._draw, 'the draw control').add(feature)
        }
      })
    } else {
      if (event?.features?.length) {
        this.addAreaPopup(event.features, this._defaultAreaUnit)
      } else {
        // Delete all areaPopups
        for (const areaPopup of this.areaPopups) {
          areaPopup.popup.remove()
        }
      }
    }
  }

  private addAreaPopup(features: GeoJSON.Feature[], defaultAreaUnit?: string) {
    // Store defaultAreaUnit if provided (first time from createEditableBoundaries)
    if (defaultAreaUnit && !this._defaultAreaUnit) {
      this._defaultAreaUnit = defaultAreaUnit
    }

    // Fallback if still not set (should not happen but safety net)
    if (!this._defaultAreaUnit) {
      this._defaultAreaUnit = this._constantService.areaUnit?.[0]?.code ?? 'm2'
    }

    const unitLabel = this._constantService.areaUnit.find(
      (constant) => constant.code === this._defaultAreaUnit,
    )?.translation

    for (const feature of features) {
      const polygon = feature.geometry as GeoJSON.Polygon
      const position = polygon?.coordinates[0]
      const turfArea: number = area(feature)
      const polygonId = feature.id

      // default turf's area unit is square meters
      // retrieve user's region's defaultAreaUnit and convert
      let convertedArea = null
      if (this._defaultAreaUnit) {
        convertedArea = convert(turfArea)
          .from('m2')
          // area unit codes come from the API constants, not checked against convert-units
          .to(this._defaultAreaUnit as AreaUnits)
      }

      // restrict to area to 2 decimal points
      const roundedArea: number = Math.round((convertedArea || turfArea) * 100) / 100

      // Remove existing popup with same id (avoid duplicates when multiple listeners fire)
      const existing = this.areaPopups.filter((p) => p.id === polygonId)
      for (const p of existing) {
        p.popup.remove()
      }
      this.areaPopups = this.areaPopups.filter((p) => p.id !== polygonId)

      this.areaPopups.push({
        id: polygonId,
        popup: new this.mapboxgl.Popup({
          closeOnClick: false,
        })
          .setLngLat([position[0][0], position[0][1]])
          .setHTML(`${roundedArea.toString()} ${unitLabel}`)
          .addTo(this._requireMap()),
      })
    }
  }

  // =====================================================================
  // MAP STYLE CONTROLS
  // =====================================================================

  /**
   * Save all current sources and their data
   */
  private saveAllSources(): Map<string, SavedGeoJSONSource> {
    // background, sky and slot layers have no source: they are looked up with an undefined key
    const savedSources: Map<string, SavedGeoJSONSource> & ReadonlyMap<string | undefined, SavedGeoJSONSource> = new Map<
      string,
      SavedGeoJSONSource
    >()

    const map = this._map
    if (!map) {
      return savedSources
    }

    const style = map.getStyle()
    if (!style || !style.sources) {
      return savedSources
    }

    // Iterate through all sources and save GeoJSON source data
    Object.keys(style.sources).forEach((sourceName) => {
      const source = map.getSource(sourceName)
      if (source && source.type === 'geojson') {
        const geoJsonSource = source as mapboxgl.GeoJSONSource
        // Save the source data by getting it from the map
        savedSources.set(sourceName, {
          type: 'geojson',
          data: geoJsonSource._data, // Access internal data
        })
      }
    })

    // Also save layer information
    if (style.layers) {
      style.layers.forEach((layer) => {
        const existingData = savedSources.get(layer.source)
        if (existingData) {
          if (!existingData.layers) {
            existingData.layers = []
          }
          existingData.layers.push(layer)
        }
      })
    }

    this._logger.debug(`Map - Saved ${savedSources.size} sources before style change`)
    return savedSources
  }

  /**
   * Restore all sources and layers after style change
   */
  private restoreAllSources(savedSources: Map<string, SavedGeoJSONSource>): void {
    const map = this._map
    if (!map || savedSources.size === 0) {
      return
    }

    // Restore each source and its layers
    savedSources.forEach((sourceData, sourceName) => {
      try {
        // Add the source back
        if (!map.getSource(sourceName)) {
          map.addSource(sourceName, {
            type: 'geojson',
            data: sourceData.data,
          })
        }

        // Restore layers for this source
        if (sourceData.layers) {
          sourceData.layers.forEach((layer) => {
            if (!map.getLayer(layer.id)) {
              map.addLayer(layer)
            }
          })
        }
      } catch (error) {
        this._logger.error(`Map - Error restoring source ${sourceName}:`, error)
      }
    })

    this._logger.debug(`Map - Restored ${savedSources.size} sources after style change`)
  }

  /**
   * Toggle between satellite and standard map styles
   */
  public toggleMapStyle(): void {
    this.setMapStyle(this.currentStyleType === 'standard' ? 'satellite' : 'standard')
  }

  /**
   * Set a specific map style
   * @param styleType - 'standard' or 'satellite'
   */
  public setMapStyle(styleType: MapStyleType): void {
    const map = this._map
    if (!map) {
      this._logger.warn('Map - Cannot set style: map not initialized')
      return
    }

    if (this.currentStyleType === styleType) {
      return // Already using this style
    }

    this.currentStyleType = styleType
    const newStyle = styleType === 'standard' ? this.standardStyle : this.satelliteStyle

    // Update control icon
    if (this.styleControl) {
      this.styleControl.setStyle(styleType)
    }

    // Store the current map state
    const center = map.getCenter()
    const zoom = map.getZoom()
    const pitch = map.getPitch()
    const bearing = map.getBearing()

    // Save all current sources and layers data
    const savedSources = this.saveAllSources()

    // Change the style
    map.setStyle(newStyle)

    // Restore map state and layers after style loads
    map.once('style.load', () => {
      map.jumpTo({
        center: center,
        zoom: zoom,
        pitch: pitch,
        bearing: bearing,
      })

      // Restore all sources and layers
      this.restoreAllSources(savedSources)

      this._logger.debug(`Map - Style changed to: ${styleType}`)
    })
  }

  /**
   * Get the current style type
   */
  public getCurrentStyleType(): MapStyleType {
    return this.currentStyleType
  }
}
